import { useEffect, useRef } from "react";

const FAR_BUILDINGS = [
  { x: 0, w: 90, h: 190 }, { x: 100, w: 70, h: 260 }, { x: 180, w: 110, h: 150 },
  { x: 300, w: 80, h: 300 }, { x: 390, w: 100, h: 210 }, { x: 500, w: 60, h: 320 },
  { x: 570, w: 120, h: 170 }, { x: 700, w: 85, h: 280 }, { x: 795, w: 95, h: 190 },
  { x: 900, w: 70, h: 250 }, { x: 980, w: 110, h: 160 }, { x: 1100, w: 80, h: 300 },
  { x: 1190, w: 95, h: 220 }, { x: 1295, w: 65, h: 270 }, { x: 1370, w: 105, h: 180 },
  { x: 1485, w: 80, h: 310 }, { x: 1575, w: 90, h: 200 },
];

const NEAR_BUILDINGS = [
  { x: 0, w: 140, h: 150, sign: null }, { x: 160, w: 120, h: 200, sign: "GROMZA" },
  { x: 300, w: 160, h: 130, sign: null }, { x: 480, w: 130, h: 230, sign: null },
  { x: 630, w: 150, h: 160, sign: "BAZAAR" }, { x: 800, w: 120, h: 190, sign: null },
  { x: 940, w: 160, h: 140, sign: null }, { x: 1120, w: 130, h: 210, sign: "GROMZA" },
  { x: 1270, w: 150, h: 150, sign: null }, { x: 1440, w: 140, h: 180, sign: null },
];

const Stars = ({ count = 70 }) => {
  const stars = Array.from({ length: count }, (_, i) => ({
    left: (i * 37.7) % 100,
    top: ((i * 53.3) % 55),
    size: (i % 3) + 1,
    delay: (i % 7) * 0.6,
  }));
  return (
    <>
      {stars.map((s, i) => (
        <span
          key={i}
          className="absolute rounded-full bg-amber-100 animate-glow"
          style={{ left: `${s.left}%`, top: `${s.top}%`, width: s.size, height: s.size, opacity: 0.35, animationDelay: `${s.delay}s` }}
        />
      ))}
    </>
  );
};

const Windows = ({ buildings, baseY, perRow = 3, opacity = 0.4 }) =>
  buildings.flatMap((b, bi) => {
    const rows = Math.floor(b.h / 42);
    return Array.from({ length: rows * perRow }, (_, i) => {
      const row = Math.floor(i / perRow);
      const col = i % perRow;
      const lit = (bi * 13 + i * 7) % 5 < 2;
      return (
        <rect
          key={`${bi}-${i}`}
          x={b.x + 14 + col * ((b.w - 24) / perRow)}
          y={baseY - b.h + 20 + row * 40}
          width="9"
          height="13"
          rx="1.5"
          fill={lit ? "#fbbf24" : "#1e293b"}
          opacity={lit ? opacity : 0.5}
        />
      );
    });
  });

export const CityBackground = () => {
  const riderRef = useRef(null);
  const trailRef = useRef(null);
  const droneRef = useRef(null);
  const farRef = useRef(null);
  const nearRef = useRef(null);
  const pinRef = useRef(null);
  const wheelRefs = useRef([]);

  useEffect(() => {
    const isMobile = window.innerWidth < 768;
    let target = 0;
    let current = 0;
    let raf = null;

    const apply = () => {
      const w = window.innerWidth;
      const x = -180 + current * (w * 0.92 + 260);
      if (riderRef.current) {
        riderRef.current.style.transform = `translate3d(${x}px, 0, 0)`;
      }
      if (trailRef.current) {
        trailRef.current.style.width = `${Math.max(0, x + 100)}px`;
        trailRef.current.style.opacity = Math.min(0.85, current * 2.4);
      }
      if (isMobile) {
        if (pinRef.current) pinRef.current.style.opacity = Math.max(0, Math.min(1, (current - 0.5) * 2.4));
        return;
      }
      wheelRefs.current.forEach((wheel) => {
        if (wheel) wheel.style.transform = `rotate(${x * 2.2}deg)`;
      });
      if (droneRef.current) {
        droneRef.current.style.transform = `translate3d(${w - 140 - current * (w + 260)}px, ${Math.sin(current * 14) * 16}px, 0)`;
      }
      if (farRef.current) farRef.current.style.transform = `translate3d(${-current * 55}px, 0, 0)`;
      if (nearRef.current) nearRef.current.style.transform = `translate3d(${-current * 130}px, 0, 0)`;
      if (pinRef.current) pinRef.current.style.opacity = Math.max(0, Math.min(1, (current - 0.5) * 2.4));
    };

    const loop = () => {
      current += (target - current) * (isMobile ? 0.14 : 0.08);
      if (Math.abs(target - current) < 0.0005) {
        current = target;
        apply();
        raf = null;
        return;
      }
      apply();
      raf = requestAnimationFrame(loop);
    };

    const onScroll = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      target = max > 0 ? window.scrollY / max : 0;
      if (raf === null) raf = requestAnimationFrame(loop);
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (raf !== null) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <div data-testid="city-background" className="fixed inset-0 z-0 overflow-hidden pointer-events-none" aria-hidden="true">
      <div className="absolute inset-0 bg-[#060913]" />
      <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse 80% 55% at 50% -10%, rgba(251,191,36,0.09), transparent 60%)" }} />
      <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, #060913 0%, #0b0f19 55%, #0a0e1c 100%)" }} />
      <Stars count={typeof window !== "undefined" && window.innerWidth < 768 ? 28 : 70} />

      {/* far skyline */}
      <div ref={farRef} className="absolute bottom-[96px] left-0 will-change-transform" style={{ width: "1700px" }}>
        <svg width="1700" height="340" viewBox="0 0 1700 340">
          {FAR_BUILDINGS.map((b, i) => (
            <rect key={i} x={b.x} y={340 - b.h} width={b.w} height={b.h} fill="#101a30" />
          ))}
          <Windows buildings={FAR_BUILDINGS} baseY={340} opacity={0.35} />
          <polygon points="530,20 540,20 535,0" fill="#101a30" />
          <circle cx="535" cy="14" r="2.5" fill="#fbbf24" opacity="0.7" />
        </svg>
      </div>

      {/* near skyline with shopfronts */}
      <div ref={nearRef} className="absolute bottom-[96px] left-0 will-change-transform" style={{ width: "1650px" }}>
        <svg width="1650" height="260" viewBox="0 0 1650 260">
          {NEAR_BUILDINGS.map((b, i) => (
            <g key={i}>
              <rect x={b.x} y={260 - b.h} width={b.w} height={b.h} fill="#0a0f1e" stroke="#fbbf24" strokeOpacity="0.06" />
              <rect x={b.x + 10} y={232} width={b.w - 20} height={28} rx="4" fill="#fbbf24" opacity="0.12" />
              {b.sign && (
                <text x={b.x + b.w / 2} y={250} textAnchor="middle" fontSize="11" fontWeight="700" letterSpacing="3" fill="#fbbf24" opacity="0.8" fontFamily="Outfit, sans-serif">
                  {b.sign}
                </text>
              )}
            </g>
          ))}
          <Windows buildings={NEAR_BUILDINGS} baseY={260} perRow={3} opacity={0.5} />
        </svg>
      </div>

      {/* road */}
      <div className="absolute bottom-0 inset-x-0 h-[96px] bg-[#070a12] border-t border-amber-400/10">
        <div className="absolute top-1/2 inset-x-0 border-t-2 border-dashed border-amber-400/25" />
      </div>

      {/* golden light trail following the rider */}
      <div
        ref={trailRef}
        className="absolute bottom-[64px] left-0 h-[3px] opacity-0"
        style={{
          background: "linear-gradient(to right, transparent, rgba(251,191,36,0.7) 60%, #fde68a)",
          boxShadow: "0 0 18px rgba(251,191,36,0.6), 0 0 42px rgba(251,191,36,0.3)",
        }}
      />

      {/* delivery rider on scooter */}
      <div ref={riderRef} className="absolute bottom-[34px] left-0 will-change-transform" style={{ width: 150 }}>
        <svg width="150" height="104" viewBox="0 0 150 104">
          <polygon points="116,46 150,40 150,62 116,58" fill="#fde68a" opacity="0.18" />
          <circle cx="117" cy="52" r="4" fill="#fde68a" />
          <rect x="10" y="30" width="34" height="30" rx="6" fill="#fbbf24" />
          <text x="27" y="51" textAnchor="middle" fontSize="17" fontWeight="700" fill="#0b0f19" fontFamily="Outfit, sans-serif">G</text>
          <circle cx="76" cy="24" r="8" fill="#1e293b" stroke="#fbbf24" strokeWidth="1.5" />
          <path d="M76 32 L68 58" stroke="#1e293b" strokeWidth="9" strokeLinecap="round" />
          <path d="M70 44 L96 50" stroke="#1e293b" strokeWidth="6" strokeLinecap="round" />
          <path d="M58 84 C58 64 70 56 86 56 L100 56 L112 84" fill="none" stroke="#223252" strokeWidth="7" strokeLinecap="round" />
          <line x1="96" y1="56" x2="110" y2="34" stroke="#fbbf24" strokeWidth="4" strokeLinecap="round" />
          <g ref={(el) => (wheelRefs.current[0] = el)} style={{ transformBox: "fill-box", transformOrigin: "center" }}>
            <circle cx="34" cy="84" r="15" fill="#0f172a" stroke="#fbbf24" strokeWidth="2.5" />
            <line x1="34" y1="71" x2="34" y2="97" stroke="#fbbf24" strokeWidth="1.5" opacity="0.7" />
            <line x1="21" y1="84" x2="47" y2="84" stroke="#fbbf24" strokeWidth="1.5" opacity="0.7" />
          </g>
          <g ref={(el) => (wheelRefs.current[1] = el)} style={{ transformBox: "fill-box", transformOrigin: "center" }}>
            <circle cx="122" cy="84" r="15" fill="#0f172a" stroke="#fbbf24" strokeWidth="2.5" />
            <line x1="122" y1="71" x2="122" y2="97" stroke="#fbbf24" strokeWidth="1.5" opacity="0.7" />
            <line x1="109" y1="84" x2="135" y2="84" stroke="#fbbf24" strokeWidth="1.5" opacity="0.7" />
          </g>
        </svg>
      </div>

      {/* delivery drone flying the return path (desktop only for mobile perf) */}
      <div ref={droneRef} className="absolute top-[130px] left-0 will-change-transform hidden md:block" style={{ width: 84 }}>
        <svg width="84" height="56" viewBox="0 0 84 56">
          <ellipse cx="10" cy="8" rx="11" ry="3" fill="#fbbf24" opacity="0.35" />
          <ellipse cx="74" cy="8" rx="11" ry="3" fill="#fbbf24" opacity="0.35" />
          <line x1="10" y1="10" x2="30" y2="20" stroke="#334155" strokeWidth="3" />
          <line x1="74" y1="10" x2="54" y2="20" stroke="#334155" strokeWidth="3" />
          <rect x="28" y="16" width="28" height="12" rx="5" fill="#1e293b" stroke="#fbbf24" strokeWidth="1.2" />
          <circle cx="42" cy="22" r="2.5" fill="#fbbf24" className="animate-glow" />
          <line x1="42" y1="28" x2="42" y2="38" stroke="#475569" strokeWidth="1.5" />
          <rect x="34" y="38" width="16" height="13" rx="3" fill="#fbbf24" />
          <text x="42" y="48" textAnchor="middle" fontSize="9" fontWeight="700" fill="#0b0f19" fontFamily="Outfit, sans-serif">G</text>
        </svg>
      </div>

      {/* customer destination pin */}
      <div ref={pinRef} className="absolute right-[7%] bottom-[150px] opacity-0">
        <svg width="46" height="60" viewBox="0 0 46 60">
          <path d="M23 2 C11 2 3 11 3 22 C3 37 23 56 23 56 C23 56 43 37 43 22 C43 11 35 2 23 2 Z" fill="#fbbf24" style={{ filter: "drop-shadow(0 0 14px rgba(251,191,36,0.7))" }} />
          <circle cx="23" cy="21" r="8" fill="#0b0f19" />
          <text x="23" y="25" textAnchor="middle" fontSize="10" fontWeight="700" fill="#fbbf24" fontFamily="Outfit, sans-serif">G</text>
        </svg>
        <div className="mx-auto mt-1 w-10 h-2 rounded-full bg-amber-400/40 blur-sm animate-glow" />
      </div>
    </div>
  );
};
